import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { FaRegTrashAlt } from "react-icons/fa";
import { AiOutlineSearch } from "react-icons/ai";
import { BACKEND_URL_PREFIX, itemsOnPage } from "../../utils/const";
import { arrayBufferToBase64 } from "../../actions/helper";
import { usePermission } from "../../hooks";
import HaventPermission from "../../assets/HaventPermission";
import usePageWidth from "../shared/usePageWidth";
import CustomSelect from "../CustomSelect";
import { getCurrentYearNow } from '../../utils/whereInTime';
import styles from './styles.module.css'

// «Все» у фильтров отбора — прежнее строковое 'all', как его ждут запросы
// справочников и метод списка; CustomSelect отдаёт пустую строку, поэтому
// значения переводятся туда и обратно в местах подключения селектов.
const ALL = 'all';

const STATUS_OPTIONS = [
  { value: 'current', name: 'Текущие' },
  { value: 'deducted', name: 'Отчислены' },
  { value: 'graduates', name: 'Выпускники' },
  { value: 'archieved', name: 'Архивная запись' },
  { value: 'new', name: 'Новый' },
]

// поле «Поиск по ФИО» не принимает больше 50 символов
const SEARCH_MAX = 50;

// «5 факультет» идёт перед «15 кафедра», «921» перед «10072/03»;
// регистр и ё/е на порядок не влияют
const COLLATE = { numeric: true, sensitivity: 'base' };

const text = (value) => String(value ?? '').trim();

const fioOf = (item) => text(item['Фамилия Имя Отчество'])
  || [item.lastname, item.firstname, item.patronymic].map(text).filter(Boolean).join(' ');

// Курс, факультет и кафедра методом списка сегодня не отдаются: они участвуют
// только в отборе (см. свагер «Состав записи»). Читаются мягко — по нескольким
// именам полей, чтобы колонки заполнились сами, как только сервер начнёт их
// отдавать; до тех пор ячейки остаются пустыми.
const courseOf = (item) => text(item['Курс'] ?? item.course ?? item.ey_name ?? item.kurs);
const facultyOf = (item) => text(item['Факультет'] ?? item.faculty ?? item.facultyname);
const cathedraOf = (item) => text(item['Кафедра'] ?? item.cathedra ?? item.cathedraname);

// Дата рождения приходит в формате RFC («Sun, 01 Mar 2009 00:00:00 GMT»).
// Разбирается по UTC: местное смещение не должно сдвигать дату на сутки назад.
const formatDate = (value) => {
  const raw = text(value);
  if (!raw) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) return raw;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
};

// Дата сортируется как дата: 01.12.1992 позже 05.11.1988
const dateKey = (value) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(formatDate(value));
  return m ? Number(`${m[3]}${m[2]}${m[1]}`) : -1;
};

const sortValue = (item, key) => {
  switch (key) {
    case 'fio': return fioOf(item).toLowerCase();
    case 'group': return text(item.groupname).toLowerCase();
    case 'course': return courseOf(item).toLowerCase();
    case 'faculty': return facultyOf(item).toLowerCase();
    case 'cathedra': return cathedraOf(item).toLowerCase();
    case 'category': return text(item['Категория']).toLowerCase();
    case 'status': return text(item['Статус']).toLowerCase();
    case 'birthday': return dateKey(item.birthday);
    default: return '';
  }
};

const isEmptyKey = (value) => value === '' || value === -1;

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
  if (pages <= 1) return [];
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
  if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
  const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

  const items = [];
  list.forEach((value, i) => {
    if (i) {
      const gap = value - list[i - 1];
      if (gap === 2) items.push(list[i - 1] + 1);
      else if (gap > 2) items.push('gap');
    }
    items.push(value);
  });
  return items;
};

const VariableStuff = ({
  data,
  fetchStudents,
  token,
  pagesNum,
  totalCount,
  faculty,
  fetchFaculty,
  educationYear,
  fetchEducationYear,
  groups,
  fetchGroup,
  loading,
  fetchStudyYear,
  studyYear,
  fetchCathedras,
  cathedras
}) => {

  usePageWidth('pageWide');

  const { readRight } = usePermission('Справочники', 'Переменный состав')

  const [numberPage, setNumberPage] = useState(0);
  const [status, setStatus] = useState('current');
  const [year, setYear] = useState('');
  const [findStudent, setFindStudent] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [findFaculty, setFindFaculty] = useState(ALL);
  const [cathedra, setCathedra] = useState('')
  const [findKurs, setFindKurs] = useState(ALL);
  const [findGroup, setFindGroup] = useState(ALL);
  // по макету список открывается отсортированным по ФИО по возрастанию
  const [sort, setSort] = useState({ key: 'fio', dir: 'asc' });
  // фотографии текущей страницы: mid → data:image/...; пока не пришла — пустой кружок
  const [photos, setPhotos] = useState({});
  const [viewer, setViewer] = useState(null);
  const [viewerFailed, setViewerFailed] = useState(false);
  // запрос уже отправлен, но isLoading ещё не поднялся (fetch ждёт checkAndUpdateToken) —
  // без этого флага на кадр между отправкой и ответом мигает пустое состояние
  const [pending, setPending] = useState(true);

  const tableRef = useRef(null);
  const firstData = useRef(true);
  // прокрутить к началу таблицы просят при смене страницы, но выполнить это
  // можно только когда таблица снова окажется в разметке (см. эффект ниже)
  const scrollToTable = useRef(false);
  // номер «поколения» загрузки фото: ответы предыдущей страницы не должны
  // дописываться к новой — иначе миниатюры перемешиваются при быстром листании
  const photoRun = useRef(0);

  const newArchieved = status === 'archieved' || status === 'new'
  const deductedGraduates = status === 'deducted' || status === 'graduates'

  useEffect(() => {
    fetchFaculty()
    fetchStudyYear()
  }, [])

  useEffect(() => {
    if (!studyYear) return;
    setYear(getCurrentYearNow(studyYear))
  }, [studyYear])

  useEffect(() => {
    if (status === 'current') return;
    setNumberPage(0)
    setFindFaculty(ALL)
    setCathedra("")
    setFindKurs(ALL)
    setFindGroup(ALL)
    if (newArchieved) {
      setYear(getCurrentYearNow(studyYear))
    }
  }, [status])

  useEffect(() => {
    setNumberPage(0)
    if (!year) return;
    const params = {
      year: year,
      idcathedra: cathedra,
      eyid: findKurs || 0,
      idfaculty: findFaculty
    }

    fetchGroup(params)

  }, [findFaculty, findKurs, cathedra, year])

  // сброс кафедры делается в обработчике селекта факультета, чтобы список курсов
  // не запрашивался дважды (сначала со старой кафедрой, потом с пустой)
  useEffect(() => {
    fetchCathedras(findFaculty == ALL ? 0 : findFaculty, true, true);
  }, [findFaculty])

  useEffect(() => {
    if (!year) return;
    fetchEducationYear({ xp_key: year, idfaculty: findFaculty, idcathedra: cathedra })
  }, [year, findFaculty, cathedra])

  useEffect(() => {
    if (findKurs === ALL) return;
    const exists = educationYear?.some(i => String(i.number) === String(findKurs))
    if (!exists) setFindKurs(ALL)
  }, [educationYear])

  // смена любого условия отбора сбрасывает список на первую страницу
  useEffect(() => {
    setNumberPage(0)
  }, [findStudent, findFaculty, findKurs, findGroup, status, year, cathedra]);

  useEffect(() => {
    // «Год» — обязательное условие отбора: без него метод списка отвечает 400.
    // Пока учебный год не определён, запроса нет и ждать нечего
    if (!year) {
      setPending(false);
      return;
    }
    setPending(true);
    fetchStudents(numberPage, findStudent, findFaculty, findKurs, findGroup, status, year, cathedra);
  }, [numberPage, findStudent, findFaculty, findKurs, findGroup, status, year, cathedra]);

  useEffect(() => {
    if (firstData.current) {
      firstData.current = false;
      return;
    }
    setPending(false);
  }, [data]);

  // поиск по ФИО работает по мере ввода, с задержкой 300 мс
  useEffect(() => {
    const timeout = setTimeout(() => setFindStudent(searchInput), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchPhoto = useCallback(async (itemId) => {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}/users/${itemId}/photo`,
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          Authorization: `Basic ${btoa(`${token}:empty`)}`,
        },
      }
    );

    if (!response.ok) return undefined;
    const buffer = await response.arrayBuffer();
    // пустое тело = фотографии нет: base64 от нуля байт даёт битую картинку
    if (!buffer || !buffer.byteLength) return undefined;
    return arrayBufferToBase64(buffer);
  }, [token]);

  // фотографии запрашиваются только для строк текущей страницы и только там,
  // где запись сама говорит, что фотография есть (поле «Фото»)
  useEffect(() => {
    const run = photoRun.current + 1;
    photoRun.current = run;
    setPhotos({});
    if (!Array.isArray(data) || !data.length) return;

    (async () => {
      for (const item of data) {
        if (photoRun.current !== run) return;
        if (item['Фото'] === false) continue;
        const src = await fetchPhoto(item.mid);
        if (photoRun.current !== run) return;
        if (src) setPhotos(prev => ({ ...prev, [item.mid]: src }));
      }
    })();
  }, [data, fetchPhoto]);

  const yearOptions = useMemo(() => (Array.isArray(studyYear) ? studyYear : [])
    .map(i => ({ name: i.name || i.label || String(i.xp_key), value: String(i.xp_key) })),
    [studyYear]);

  const facultyOptions = useMemo(() => (Array.isArray(faculty) ? faculty : [])
    .map(i => ({ name: i.faculty || i.name || '', value: String(i.idfaculty) }))
    .filter(i => i.name),
    [faculty]);

  // перечень кафедр приходит уже со своим пунктом «Все» (idcathedra = 0)
  const cathedraOptions = useMemo(() => (Array.isArray(cathedras) ? cathedras : [])
    .map(i => ({ name: i.cathedra || i.name || '', value: String(i.idcathedra) }))
    .filter(i => i.name),
    [cathedras]);

  const courseOptions = useMemo(() => (Array.isArray(educationYear) ? educationYear : [])
    .map(i => ({ name: i.ey_name || String(i.number), value: String(i.number) })),
    [educationYear]);

  const groupOptions = useMemo(() => (Array.isArray(groups) ? groups : [])
    .map(i => ({ name: i.group_name || i.name || '', value: String(i.gid) }))
    .filter(i => i.name),
    [groups]);

  // сортировка идёт по загруженной странице: у метода списка нет параметра сортировки
  const rows = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    if (!sort) return list;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (isEmptyKey(va) && isEmptyKey(vb)) return 0;
      if (isEmptyKey(va)) return 1;
      if (isEmptyKey(vb)) return -1;
      if (typeof va === 'number') return (va - vb) * dir;
      return va.localeCompare(vb, 'ru', COLLATE) * dir;
    });
  }, [data, sort]);

  // повторный клик по той же колонке меняет направление
  const handleSort = (key) => {
    setSort(prev => (prev && prev.key === key)
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  // Прокрутка откладывается до появления новой страницы списка: на время запроса
  // таблица уступает место загрузчику, страница становится короткой, и прокрутка,
  // запущенная в момент клика, обрывается — окно остаётся у низа таблицы.
  const handlePageChange = (page) => {
    if (page === numberPage) return;
    setNumberPage(page);
    setPending(true);
    scrollToTable.current = true;
  };

  useEffect(() => {
    if (!scrollToTable.current || loading || pending) return;
    scrollToTable.current = false;
    if (tableRef.current) tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loading, pending, rows]);

  const defaultYear = getCurrentYearNow(studyYear);
  const isFiltersDirty = status !== 'current'
    || String(year) !== String(defaultYear)
    || findFaculty !== ALL
    || cathedra !== ''
    || findKurs !== ALL
    || findGroup !== ALL
    || !!searchInput
    || !!findStudent;

  const clearFiltersHandler = () => {
    setStatus('current')
    setYear(defaultYear)
    setSearchInput("")
    setFindStudent("")
    setFindFaculty(ALL)
    setCathedra("")
    setFindKurs(ALL)
    setFindGroup(ALL)
    setNumberPage(0)
  }

  const closeViewer = useCallback(() => {
    setViewer(null);
    setViewerFailed(false);
  }, []);

  const openViewer = (item) => {
    if (!photos[item.mid]) return; // пустой кружок не открывает ничего
    setViewerFailed(false);
    setViewer(item);
  };

  // пока окно открыто, страница под ним не прокручивается; ширина исчезнувшего
  // скроллбара компенсируется отступом, чтобы содержимое не дёрнулось вправо
  useEffect(() => {
    if (!viewer) return undefined;
    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeViewer();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewer, closeViewer]);

  // каретка есть у всех сортируемых колонок; у неактивных — приглушённая ▲
  const renderHead = (key, label, className) => {
    const active = sort.key === key;
    return (
      <th
        className={className ? `${styles.sortable} ${className}` : styles.sortable}
        onClick={() => handleSort(key)}
        role="button"
      >
        {label}
        <span className={active ? styles.caretOn : styles.caret}>
          {active && sort.dir === 'desc' ? '▼' : '▲'}
        </span>
      </th>
    );
  };

  // незаполненное значение выводится пустой ячейкой: прочерки не подставляются
  const renderClamped = (value) => value
    ? <span className={styles.clamp2} title={value}>{value}</span>
    : null;

  const renderRow = (item) => {
    const src = photos[item.mid];
    const fio = fioOf(item);
    return (
      <tr key={item.mid}>
        <td className={styles.fio}>{renderClamped(fio)}</td>
        <td className={styles.num}>{text(item.groupname)}</td>
        <td className={styles.num}>{courseOf(item)}</td>
        <td className={styles.left}>{renderClamped(facultyOf(item))}</td>
        <td className={styles.left}>{renderClamped(cathedraOf(item))}</td>
        <td>{text(item['Категория'])}</td>
        <td>{renderClamped(text(item['Статус']))}</td>
        <td className={styles.num}>{formatDate(item.birthday)}</td>
        <td className={styles.colPhoto}>
          {src
            ? (
              <button
                type="button"
                className={`${styles.ava} ${styles.avaHas}`}
                onClick={() => openViewer(item)}
                title="Открыть фотографию"
              >
                <img className={styles.avaImg} src={src} alt="" />
              </button>
            )
            : <div className={`${styles.ava} ${styles.avaNone}`} />}
        </td>
      </tr>
    )
  }

  const viewerNode = viewer && createPortal(
    <div className={styles.photoOv} onClick={closeViewer}>
      <div className={styles.photoBox} onClick={e => e.stopPropagation()}>
        <div className={styles.photoPic}>
          {photos[viewer.mid] && !viewerFailed
            ? (
              <img
                src={photos[viewer.mid]}
                alt={fioOf(viewer)}
                onError={() => setViewerFailed(true)}
              />
            )
            : <div className={styles.photoFail}>Фотография недоступна</div>}
        </div>
        <div className={styles.photoCap}>
          <div className={styles.photoName}>{fioOf(viewer)}</div>
          <div className={styles.photoPos}>
            {[text(viewer['Категория']), text(viewer.groupname), facultyOf(viewer)]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <div className={styles.photoFoot}>
          <button type="button" onClick={closeViewer}>Закрыть</button>
        </div>
      </div>
    </div>,
    document.body
  );

  if (!readRight) return <HaventPermission />

  const fromRecord = totalCount ? numberPage * itemsOnPage + 1 : 0;
  const toRecord = Math.min((numberPage + 1) * itemsOnPage, totalCount || 0);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Переменный состав</h1>
        <div className={styles.headRight}>
          <button
            className={styles.resetBtn}
            onClick={clearFiltersHandler}
            disabled={!isFiltersDirty}
          >
            Сбросить фильтры
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.fStatus}>
          <CustomSelect
            title="Статус"
            width="100%"
            options={STATUS_OPTIONS}
            value={status}
            setFieldValue={(name, value) => setStatus(value || 'current')}
            valuesName="status"
            withoutAll
          />
        </div>
        <div className={styles.fYear}>
          <CustomSelect
            title="Год"
            width="100%"
            options={yearOptions}
            value={String(year)}
            setFieldValue={(name, value) => setYear(value || defaultYear)}
            valuesName="year"
            withoutAll
            disabled={newArchieved}
          />
        </div>
        <div className={styles.fFaculty}>
          <CustomSelect
            title="Факультет"
            width="100%"
            options={facultyOptions}
            value={findFaculty === ALL ? '' : String(findFaculty)}
            setFieldValue={(name, value) => {
              setFindFaculty(value || ALL)
              setCathedra("")
            }}
            valuesName="faculty"
            disabled={newArchieved || deductedGraduates}
          />
        </div>
        <div className={styles.fCathedra}>
          <CustomSelect
            title="Кафедра"
            width="100%"
            options={cathedraOptions}
            value={cathedra === '' ? '0' : String(cathedra)}
            setFieldValue={(name, value) => setCathedra(value === '0' ? '' : value)}
            valuesName="cathedra"
            withoutAll
            disabled={newArchieved || deductedGraduates}
          />
        </div>
        <div className={styles.fCourse}>
          <CustomSelect
            title="Курс"
            width="100%"
            options={courseOptions}
            value={findKurs === ALL ? '' : String(findKurs)}
            setFieldValue={(name, value) => setFindKurs(value || ALL)}
            valuesName="kurs"
            disabled={newArchieved || deductedGraduates}
          />
        </div>
        <div className={styles.fGroup}>
          <CustomSelect
            title="Группа"
            width="100%"
            options={groupOptions}
            value={findGroup === ALL ? '' : String(findGroup)}
            setFieldValue={(name, value) => setFindGroup(value || ALL)}
            valuesName="group"
            disabled={newArchieved || deductedGraduates}
          />
        </div>

        <div className={styles.srchWrap}>
          <span className={styles.srchLabel}>Поиск по ФИО</span>
          <AiOutlineSearch className={styles.srchIcon} size={18} />
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value.slice(0, SEARCH_MAX))}
            maxLength={SEARCH_MAX}
            placeholder="Фамилия, имя или отчество"
          />
        </div>
      </div>

      {(loading || pending) ? (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap} ref={tableRef}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {renderHead('fio', 'ФИО', styles.colFio)}
                  {renderHead('group', 'Группа', styles.colGroup)}
                  {renderHead('course', 'Курс', styles.colCourse)}
                  {renderHead('faculty', 'Факультет', styles.colFaculty)}
                  {renderHead('cathedra', 'Кафедра', styles.colCathedra)}
                  {renderHead('category', 'Категория', styles.colCategory)}
                  {renderHead('status', 'Статус', styles.colStatus)}
                  {renderHead('birthday', <>Дата<br />рождения</>, styles.colBirthday)}
                  <th className={styles.colPhoto}>Фото</th>
                </tr>
              </thead>
              <tbody>
                {/* ничего не найдено — сообщение в теле таблицы, шапка остаётся на месте */}
                {rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={9}>
                        <div className={styles.empty}>
                          <div className={styles.emptyTitle}>По заданным условиям обучающиеся не найдены</div>
                          Измените фильтры или очистите поиск
                        </div>
                      </td>
                    </tr>
                  )
                  : rows.map(renderRow)}
              </tbody>
            </table>
          </div>

          <div className={styles.footrow}>
            <span className={styles.total}>
              Найдено записей: <b>{totalCount || 0}</b>
            </span>
            {!!totalCount && (
              <span className={styles.shown}>Показаны {fromRecord}–{toRecord} из {totalCount}</span>
            )}
            {pagesNum > 1 && (
              <div className={styles.pager}>
                <button
                  disabled={numberPage === 0}
                  onClick={() => handlePageChange(numberPage - 1)}
                  aria-label="Предыдущая страница"
                >
                  ‹
                </button>
                {pagerItems(numberPage + 1, pagesNum).map((item, ind) => item === 'gap'
                  ? <span key={`gap-${ind}`} className={styles.gap}>…</span>
                  : <button
                    key={item}
                    className={item === numberPage + 1 ? styles.pageOn : undefined}
                    onClick={() => handlePageChange(item - 1)}
                  >
                    {item}
                  </button>
                )}
                <button
                  disabled={numberPage + 1 >= pagesNum}
                  onClick={() => handlePageChange(numberPage + 1)}
                  aria-label="Следующая страница"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {viewerNode}
    </div>
  )
};

export default VariableStuff;
